import {
  Button,
  Modal,
  Form,
  Input,
  Select,
  SearchBox,
  notification,
  Row,
  Col,
} from 'tea-component';
import './index.scss';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uid } from 'uuid';
import { CustomColumn } from '@/pages/datalab/pages/Multidimensional/Analysis/interface';
import type { IColumnRawInfo } from '@dbl/core/dataSource';
import { useSqlEdit } from './useSqlEdit';
import { LazyEditor } from '@/pages/datalab/components/AceEditor/LazyEditor';
import { useForm, useField } from 'react-final-form-hooks';
import type { IGetTableInfoDto } from '@dbl/services/dataSource';
import { columnCheck } from '@dbl/services/dataSource';
import { checkNullString } from '@/pages/datalab/utils/utils';
import SvgIcon from '@/pages/home/components/SvgIcon';
import MenuTab from '../components/menuTab';
import {ITableJoinRelation} from "@/pages/datalab/pages/Multidimensional/Analysis/components/HeaderData/interface";
import {isEmpty} from "lodash-es";
import { FieldTypeDefaultAccuracy } from '@dbl/core/chartEditor';
// import { NAME_REGEX } from '@dbl/core/global';

const FieldTypeOptions = [
  {
    value: 'float', // 产品要求默认2位小数, 后端要求是float类型
    text_en: 'number',
    text: '数值',
  },
  {
    value: 'string',
    text_en: 'string',
    text: '字符',
  },
  {
    value: 'date',
    text_en: 'date',
    text: '时间',
  },
];

interface ValidateMsg {
  code: number;
  msg: string;
}

function getStatus(meta: any, validating: any) {
  if (meta.active && validating) {
    return 'validating';
  }
  if (!meta.touched) {
    return undefined;
  }
  return meta.error ? 'error' : 'success';
}

const DefineField = (props: any) => {
  const {
    onClose,
    onConfirm,
    fieldValue,
    dataTables,
    customColumns,
    multiFormatMessage,
    commonFormatMessage,
    isEn,
    dataTableJoinRelations,
  }: {
    onClose: Function;
    onConfirm: Function;
    fieldValue: any;
    dataTables: IGetTableInfoDto[];
    customColumns?: CustomColumn[];
    multiFormatMessage: Function;
    commonFormatMessage: Function;
    isEn: boolean;
    dataTableJoinRelations: ITableJoinRelation[];
  } = props;

  const intlFieldTypeOptions = FieldTypeOptions.map((opt) => ({
    value: opt.value,
    text: isEn ? opt.text_en : opt.text
  }));

  const lazyEditor = useRef(null);
  const [sqlValidate, setSqlValidate] = useState<ValidateMsg>({ code: 0, msg: '' });
  const { sql, setSql, setCursorPosition, addTableColumn } = useSqlEdit(lazyEditor, fieldValue.sql_expression);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [checkLoading, setCheckLoading] = useState(false);

  const onSubmit = (values: any) => {
    setCheckLoading(true);
    checkSql((res: {
      date_format: string;
      error_msg: string;
      is_valid: boolean;
      origin_type: string;
      to_date_format: string;
    }) => {
      if (res.is_valid) {
        setSqlValidate({ code: 0, msg: '' });
        // ['percent', 'number'].includes(fieldValue?.column_type) 应该是为了兼容旧数据
        if (values.column_type === 'float') {
          values.column_type = ['percent', 'number'].includes(fieldValue?.column_type) ? fieldValue?.column_type : values.column_type;
          values.operation = 'greater';
        } else if (values.column_type === 'date') {
          values.dateType = fieldValue?.dateType || 'day';
          values.date_format = res?.date_format || 'YYYY-MM-DD';
          values.to_date_format = fieldValue?.to_date_format || res?.to_date_format || '';
          values.lastCount = fieldValue?.lastCount ?? 7;
          values.operation = 'recent';
          values.last_day = '1';
        } else {
          isEmpty(fieldValue) && (values.isAll = true);
          // isEmpty(fieldValue) 为true,说明是新增自定义字段
          // 编辑更新自定义字段时, 保持原有的字段全选状态.
        }
        
        let accuracy = fieldValue?.accuracy || FieldTypeDefaultAccuracy[values.column_type];
        // 说明切换了 字段类型
        if (['string', 'date'].includes(values.column_type) && fieldValue?.column_type !== values.column_type) {
          accuracy = 0;
        }
        
        const newValues = {
          ...values,
          name_en: values.name_cn, // name_en 仅作保存/上传字段；唯一身份为 name_cn，此处保持与 name_cn 一致
          sql_expression: sql,
          accuracy,
          id: fieldValue?.id || uid(),
          origin_type: res.origin_type,
          column_custom: true,
          is_aggregate: /\b(avg|sum|distinct|max|min|count)\s*\(/i.test(sql),
          operation: values.column_type === 'string' ? 'include' : values.operation,
        };
        onConfirm(newValues);
        notification.success({
          // fieldValue.id || fieldValue.name_en 为了兼容旧数据
          description: checkNullString(fieldValue?.id || fieldValue?.name_en) ? multiFormatMessage("filed_add_success") : multiFormatMessage("filed_edit_success"),
        });
        setCheckLoading(false);
      }
    });
  };
  const { form, handleSubmit, validating } = useForm({
    onSubmit,
    initialValuesEqual: () => true,
    validate: ({ name_cn: nameCn, column_type }: {name_cn: string; column_type: string;}) => {
      let nameCnValidate: string | undefined = undefined;
      if (!nameCn || nameCn.length <= 0) nameCnValidate = multiFormatMessage("filed_zh_valid_empty");
      else if (nameCn.length > 256) nameCnValidate = commonFormatMessage("valid_name_length", {count: '256'});
      // else if (!NAME_REGEX.test(nameCn)) nameCnValidate = commonFormatMessage("valid_name");
      else if (customColumns?.some((column: CustomColumn) => (column.name_cn === nameCn) && (column.id !== fieldValue.id))) {
        nameCnValidate = isEn ? 'Duplicate naming' : '重复命名';
      } else {
        // 数据表中是否重名
        const isColumnRepeat = dataTables.some(table => table?.column_list?.some(column => {
          const {display_name: displayName, name} = column;
            
          return displayName?.toLowerCase() === nameCn?.toLowerCase() || name?.toLocaleLowerCase() === nameCn?.toLowerCase();
        }));
        
        if (isColumnRepeat) {
          nameCnValidate = isEn ? 'Same name as data table field' : '与数据表字段重名';
        }
      }
  
      
      return {
        name_cn: nameCnValidate,
        // name_en:
        //   !name_en || name_en.length <= 0
        //     ? multiFormatMessage("filed_en_valid_empty")
        //     : customColumns
        //         ?.filter((item) => item.name_en !== originNameEn)
        //         .some((item: CustomColumn) => item.name_en === name_en)
        //     ? multiFormatMessage("filed_valid_used")
        //     : name_en.length > 24
        //     ? multiFormatMessage("filed_valid_length")
        //     : !/^\w([\s]*\w)*$/.test(name_en) // 校验规则为 前后不能有空格, 匹配任意字符, 数字, 下划线和空格, (不能有汉字)
        //     ? multiFormatMessage("filed_en_valid")
        //     : undefined,
        column_type: !column_type || column_type.length <= 0 ? multiFormatMessage("filed_type_valid") : undefined,
        remark: undefined,
      };
    },
  });

  useEffect(() => {
    form.initialize({...fieldValue, column_type: ['percent', 'number'].includes(fieldValue.column_type) ? 'float' : fieldValue.column_type});
  }, []);

  useEffect(() => {
    const columnKeyWords: string[] = [];
    dataTables.forEach((table) => {
      table.column_list?.forEach((column) => columnKeyWords.push(`${table.table_code}.${column.name}`));
    });
    setKeywords(columnKeyWords);
  }, [JSON.stringify(dataTables)]);

  const name_cn = useField('name_cn', form);
  // const name_en = useField('name_en', form);
  const column_type = useField('column_type', form);
  const remark = useField('remark', form);

  const checkSql = useCallback(
    (callback: Function | undefined) => {
      if (checkNullString(sql)) {
        setSqlValidate({ code: 1, msg: multiFormatMessage("filed_calculation_formula") });
        setCheckLoading(false);
        return;
      }
      const params = {
        table_id: dataTables[0]?.table_id || '',
        column: 'CustomField',
        custom_column: {
          custom_columns: [{
            name_cn: 'CustomField',
            column_type: 'float',
            name_en: 'CustomField',
            sql_expression: sql,
            accuracy: 2,
          }],
          data_tables: dataTables,
          table_infos: dataTableJoinRelations,
          metrics: [
            {
              table_code: 't0',
              table_id: '',
              column: 'CustomField',
              label: 'CustomField',
              label_en: 'CustomField',
              column_type: 'string',
              aggregate: '',
              accuracy: 2,
              to_date_format: '',
            }
          ]
        },
        expression: sql,
      };
      columnCheck(params)
        .then((res: any) => {
          
          if (!res.is_valid) {
            setSqlValidate({ code: 1, msg: `${multiFormatMessage("filed_sql_error")}${res.error_msg || res}` });
            // if (callback) {
            //   callback(res);
            // }
            return;
          }
          setSqlValidate({ code: 0, msg: callback ? '' : multiFormatMessage("filed_sql_success") });
          if (callback) {
            callback(res);
          }
        })
        .catch((e: any) => {
          console.log(e);
        }).finally(() => {
          setCheckLoading(false);
        });
    },
    [sql, isEn],
  );
  return (
    <>
      <form onSubmit={handleSubmit}>
        <Modal.Body className="datalabe-analysis">
          <Row className="dbl-analysis-add-field-row">
            <Col className="dbl-analysis-add-field-left" span={16}>
              <div className="add-field-title">{multiFormatMessage("field_setting")}</div>
              <div className={'lazy-editor-body'}>
                <LazyEditor
                  ref={lazyEditor}
                  colspan="2"
                  className="add-field-input"
                  aria-label="query editor input"
                  mode="mysql"
                  theme="tomorrow"
                  fontSize={13}
                  // 不设置最大行数高度, 则自动根据内容高度调整高度
                  // maxLines={9}
                  minLines={9}
                  width="100%"
                  height="176px"
                  showPrintMargin={false}
                  showGutter={false}
                  highlightActiveLine={false}
                  editorProps={{ $blockScrolling: true }}
                  setOptions={{
                    enableBasicAutocompletion: true,
                    enableLiveAutocompletion: true,
                    enableSnippets: true,
                  }}
                  value={sql}
                  onChange={(value: string) => {
                    setSql(value);
                  }}
                  onCursorChange={(value: any) => {
                    setCursorPosition([value.cursor.row, value.cursor.column]);
                  }}
                  keywordExtension={keywords}
                  placeholder={multiFormatMessage("field_setting_placeholder")}
                />
              </div>

              <div>
                <div className="add-field-btn">
                  <div
                    className="add-field-down"
                    onClick={() => {
                      checkSql(undefined);
                    }}
                  >
                    <SvgIcon className="add-field-icon" type="search-a" />
                    {multiFormatMessage("grammar_check")}
                  </div>
                  <div
                    className="add-field-down"
                    onClick={() => {
                      setSql('');
                    }}
                  >
                    {commonFormatMessage("clear")}
                  </div>
                </div>
                <div
                  className={sqlValidate.code === 0 ? 'lazy-editor-body-tips' : 'lazy-editor-body-tips-error'}
                  style={{ display: `${checkNullString(sqlValidate.msg) ? 'none' : 'block'}` }}
                >
                  {sqlValidate.msg}
                </div>
              </div>
              <Form className={"field-form"}>
                <Form.Item
                  label={multiFormatMessage("filed_zh")}
                  showStatusIcon={false}
                  status={getStatus(name_cn.meta, validating)}
                  message={getStatus(name_cn.meta, validating) === 'error' && name_cn.meta.error}
                >
                  <Input
                    {...name_cn.input}
                    className="add-field-inputitem"
                    id="field1"
                    showStatusIcon={false}
                    placeholder={multiFormatMessage("filed_zh_placeholder")}
                    autoComplete="off"
                    aria-autocomplete="none"
                  />
                </Form.Item>
                {/* <Form.Item*/}
                {/*  label={multiFormatMessage("filed_en")}*/}
                {/*  status={getStatus(name_en.meta, validating)}*/}
                {/*  showStatusIcon={false}*/}
                {/*  message={getStatus(name_en.meta, validating) === 'error' && name_en.meta.error}*/}
                {/* >*/}
                {/*  <Input*/}
                {/*    {...name_en.input}*/}
                {/*    className="add-field-inputitem"*/}
                {/*    id="field2"*/}
                {/*    showStatusIcon={false}*/}
                {/*    placeholder={multiFormatMessage("filed_en_placeholder")}*/}
                {/*    autoComplete="off"*/}
                {/*    aria-autocomplete="none"*/}
                {/*  />*/}
                {/* </Form.Item>*/}
                <Form.Item
                  label={multiFormatMessage("filed_type")}
                  showStatusIcon={false}
                  status={getStatus(column_type.meta, validating)}
                  message={getStatus(column_type.meta, validating) === 'error' && column_type.meta.error}
                >
                  <Select
                    {...column_type.input}
                    className="add-field-inputitem"
                    placeholder={multiFormatMessage("filed_type_placeholder")}
                    showStatusIcon={false}
                    appearance="button"
                    defaultValue="number"
                    options={intlFieldTypeOptions}
                    matchButtonWidth
                  />
                </Form.Item>
                <Form.Item label={multiFormatMessage('filed_note')}>
                  <Input
                    {...remark.input}
                    className="add-field-inputitem"
                    showStatusIcon={false}
                    placeholder={multiFormatMessage('filed_note_placeholder')}
                    autoComplete="off"
                  />
                </Form.Item>
              </Form>
            </Col>
            <Col span={8} className="left-border analysis-number dbl-analysis-add-field-right">
              <div className="add-field-flex">
                <div className="add-field-title">{multiFormatMessage('field_existed')}</div>
                <div className="add-field-desc">{multiFormatMessage('field_setting_info')}</div>
              </div>
              <div className="add-field-rightbox" style={{ height: '600px'}}>
                <TableFieldList
                  dataTables={dataTables}
                  addTableColumn={addTableColumn}
                  multiFormatMessage={multiFormatMessage}
                  commonFormatMessage={commonFormatMessage}
                  isEn={isEn}
                />
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Form.Action className="define-field-action">
            <Button type="primary" htmlType="submit" disabled={validating} loading={checkLoading}>
              {commonFormatMessage('confirm')}
            </Button>
            <Button
              htmlType="button"
              type="weak"
              onClick={() => {
                onClose();
              }}
            >
              {commonFormatMessage('close')}
            </Button>
          </Form.Action>
        </Modal.Footer>
      </form>
    </>
  );
};

const TableFieldList = (props: any) => {
  const { dataTables, addTableColumn, multiFormatMessage, commonFormatMessage,isEn } = props;
  const [curTables, setCurTables] = useState<IGetTableInfoDto[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');

  const showNoData = useMemo(() => {
    let showNoData = false;
    try {
      (curTables || []).forEach((tableInfo: IGetTableInfoDto) => {
        (tableInfo.column_class_list || []).forEach((colunmClassList) => {
          if (colunmClassList.columns.length > 0) {
            showNoData = false;
            throw new Error();
          }
        });
      });
      showNoData = true;
    } catch (_) {}

    return showNoData;
  }, [curTables]);

  useEffect(() => {
    setCurTables(JSON.parse(JSON.stringify(dataTables)));
  }, [dataTables]);

  function Search(value: string) {
    setSearchValue(value);
    value = value.toLowerCase();
    if (value) {
      const searchTables: IGetTableInfoDto[] = JSON.parse(JSON.stringify(dataTables));
      (searchTables || []).forEach((tableInfo: IGetTableInfoDto) => {
        {
          if (tableInfo.column_class_list) {
            for (let j = 0;j < tableInfo.column_class_list.length;j++) {
              const colunmClassList = tableInfo.column_class_list[j];
              if (colunmClassList.columns) {
                for (let k = 0;k < colunmClassList.columns.length;k++) {
                  const columns = colunmClassList.columns[k];
                  if (columns.column_display.toLowerCase().indexOf(value) === -1 && columns.column_name.toLowerCase().indexOf(value) === -1) {
                    colunmClassList.columns.splice(k, 1);
                    k = k - 1;
                  }
                }
                if (colunmClassList.columns.length === 0) {
                  tableInfo.column_class_list.splice(j, 1);
                  j = j - 1;
                }
              }
            }
          }
        }
      });

      setCurTables(searchTables);
    } else {
      setCurTables(dataTables);
    }
  }

  return curTables.length > 0 ? (
    <>
      <SearchBox className="left-search" value={searchValue} onChange={Search} placeholder={commonFormatMessage("search")} />
      {showNoData ? (
        <div style={{ textAlign: 'center', height: '100%', marginTop: '20%' }}>{commonFormatMessage("search_nothing")}</div>
      ) : (
        <div className="left-data">
          {(curTables || []).map((tableInfo: IGetTableInfoDto) => (
            // const tabInfo = {
            //   title: `${tableInfo.table_code} ${tableInfo.table_name}`,
            //   data: (tableInfo.column_class_list || []).map((columnClass: ITableColumnClassDto) => ({
            //     group: columnClass.name,
            //     icon: 'bag-alt',
            //     data: (columnClass.columns || []).map((column: IColumnRawInfo) => ({
            //       text: column.column_display,
            //       desc: column.column_name
            //     }))
            //   })),
            // }
            <MenuTab
              key={tableInfo.table_id}
              tableInfo={tableInfo}
              searchValue={searchValue}
              clickField={(e: any, columnRawInfo: IColumnRawInfo) => {
                e.preventDefault();
                // const column = columnRawInfo ? ((tableInfo.column_class_list || []).reduce((total, cur) => {
                //   total.push(...cur.columns)
                //   return total
                // }, [] as IColumnRawInfo[]) || []).find((item) => item.column_name === columnRawInfo) : undefined;
                columnRawInfo && addTableColumn(tableInfo.table_code, columnRawInfo);
              }}
              multiFormatMessage={multiFormatMessage}
              commonFormatMessage={commonFormatMessage}
              isEn={isEn}
            />
          ))}
        </div>
      )}
    </>
  ) : null;
};

export default DefineField;
